import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Card,
  CardContent,
  CardHeader,
  CardSubTitle,
  CardTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Typography,
} from '@growdash/design-system/atoms';
import { Loader2 } from 'lucide-react';
// @ts-ignore
import RestaurantLink from '../../../components/atoms/restaurant-link';
import { Branch, PaginatedResponseBranch } from '../../../generated';

type IRestaurantsData = {
  new: PaginatedResponseBranch | undefined;
  closed: PaginatedResponseBranch | undefined;
};

type Props = {
  isLoading: boolean;
  data: IRestaurantsData | undefined;
  className?: string;
};

const StackedAvatarsViews = ({
  branches,
  isClosed = false,
}: {
  branches: Branch[];
  isClosed?: boolean;
}) => {
  return (
    <div className="relative h-12 my-2">
      {branches?.slice(0, 8).map((branch: Branch, index) => (
        <RestaurantLink
          className={isClosed ? 'cursor-default pointer-events-none' : ''}
          id={branch?.branch_id}
        >
          <TooltipProvider>
            <Tooltip key={branch.branch_id} delayDuration={0}>
              <TooltipTrigger asChild>
                <Avatar
                  style={{
                    transform: `translateX(${index * 24}px)`,
                  }}
                  className={` absolute top-0 h-12 w-12 hover:opacity-80 border-border/60 border-[1px] cursor-pointer`}
                >
                  <AvatarImage
                    className={`rounded-sm h-12 w-12 ${
                      isClosed ? 'filter grayscale' : ''
                    }`}
                    src={branch.branch_image ?? ''}
                    alt={branch.branch_name}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <AvatarFallback>{branch.branch_name[0]}</AvatarFallback>
                </Avatar>
              </TooltipTrigger>
              <TooltipContent>{branch?.branch_name}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </RestaurantLink>
      ))}
      {branches?.length > 8 && (
        <div
          style={{
            transform: `translateX(${8 * 24}px)`,
          }}
          className="rounded-full bg-quarternary flex items-center justify-center cursor-default pointer-events-none absolute top-0 h-12 w-12 border-border/60 border-[1px]"
        >
          <Typography className="text-sm font-medium text-secondary">{`+${branches?.length - 8}`}</Typography>
        </div>
      )}
    </div>
  );
};

export default function NewClosedResturants({
  isLoading,
  data,
  className = '',
}: Props) {
  return (
    <Card
      className={`${className} animate-fade-in min-h-48 shadow-none overflow-x-hidden`}
    >
      <CardHeader>
        <CardTitle>Restaurants</CardTitle>
        <CardSubTitle>Last Month</CardSubTitle>
      </CardHeader>
      <CardContent>
        {!isLoading ? (
          <div className="animate-fade-in flex items-start gap-12 sm:gap-36 max-sm:flex-col">
            {/* New Branches */}
            {data?.new?.items && data?.new?.items.length > 0 && (
              <div>
                <CardTitle className="mb-4">New</CardTitle>
                <StackedAvatarsViews branches={data?.new?.items ?? []} />
                <div className="mt-4">
                  <Typography variant="caption">
                    {data?.new?.items?.length} new in last month
                  </Typography>
                </div>
              </div>
            )}
            {/* Closed Branches */}
            {data?.closed?.items && data?.closed?.items.length > 0 && (
              <div>
                <CardTitle className="mb-4">Closed</CardTitle>
                <StackedAvatarsViews
                  branches={data?.closed?.items ?? []}
                  isClosed
                />
                <div className="mt-4">
                  <Typography variant="caption">
                    {data?.closed?.items?.length} closed in last month
                  </Typography>
                </div>
              </div>
            )}
            {!data?.closed?.items?.length && !data?.new?.items?.length && (
              <div className="w-full pt-6 text-center">
                <Typography className="text-tertiary">
                  No new or closed restaurants.
                </Typography>
              </div>
            )}
          </div>
        ) : (
          <div className="w-full flex items-center justify-center pt-12 animate-pulse">
            <Loader2 className="animate-spin ease-in-out" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
